import React from 'react';
import { hasPermission, isSuperAdmin, isBusinessAdmin } from '../../utils/permissions';
import { useAuth } from '../../contexts/AuthContext';

/**
 * PermissionGate Component
 * Conditionally renders children based on user permissions
 * 
 * @param {Object} props
 * @param {string} props.feature - Feature name to check permission for
 * @param {string} props.level - Required access level ('read', 'write', 'delete', 'admin')
 * @param {React.ReactNode} props.children - Content to render if permission granted
 * @param {React.ReactNode} props.fallback - Optional content to render if permission denied
 */
export function PermissionGate({ feature, level = 'read', children, fallback = null }) {
  const { userRoles } = useAuth();

  // Super Admins bypass all permission gates
  if (isSuperAdmin(userRoles)) {
    return <>{children}</>;
  }

  const hasAccess = hasPermission(userRoles, feature, level);

  if (!hasAccess) return fallback;

  return <>{children}</>;
}

/**
 * RoleGate Component
 * Conditionally renders children based on user role
 * 
 * @param {Object} props
 * @param {Array<string>} props.roles - Array of role names that can access
 * @param {React.ReactNode} props.children - Content to render if role matches
 * @param {React.ReactNode} props.fallback - Optional content to render if role doesn't match
 */
export function RoleGate({ roles, children, fallback = null }) {
  const { userRoles } = useAuth();

  const hasRole = userRoles?.some(ra => roles?.includes(ra?.role?.name));

  if (!hasRole) return fallback;

  return <>{children}</>;
}

/**
 * AdminGate Component
 * Conditionally renders children for Super Admins and Business Admins only
 * 
 * @param {Object} props
 * @param {string} props.tenantId - Optional tenant ID for Business Admin check
 * @param {React.ReactNode} props.children - Content to render if admin
 * @param {React.ReactNode} props.fallback - Optional content to render if not admin
 */
export function AdminGate({ tenantId = null, children, fallback = null }) {
  const { userRoles } = useAuth();

  // Super Admins always pass - no tenant restrictions
  if (isSuperAdmin(userRoles)) {
    return <>{children}</>;
  }

  // Business Admins must match tenant (if specified)
  const isAdmin = isBusinessAdmin(userRoles, tenantId);

  if (!isAdmin) return fallback;

  return <>{children}</>;
}

/**
 * TenantGate Component
 * Conditionally renders children based on tenant access
 * 
 * @param {Object} props
 * @param {string} props.resourceTenantId - Tenant ID of the resource
 * @param {React.ReactNode} props.children - Content to render if access granted
 * @param {React.ReactNode} props.fallback - Optional content to render if access denied
 */
export function TenantGate({ resourceTenantId, children, fallback = null }) {
  const { profile, userRoles } = useAuth();

  // Super admins can access all tenants - bypass all restrictions
  if (isSuperAdmin(userRoles)) {
    return <>{children}</>;
  }

  // Check if user's tenant matches resource tenant
  const hasAccess = profile?.tenant_id === resourceTenantId;

  if (!hasAccess) return fallback;

  return <>{children}</>;
}